import { type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import useIncomingPostMessages from '@rio-cloud/rio-uikit/useIncomingPostMessages';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import IframeResizer from 'iframe-resizer-react';
import { useAppSelector } from '../../../configuration/setup/hooks.ts';
import { getIdToken } from '../../../configuration/tokenHandling/tokenSlice.ts';
import { config } from '../../../config.ts';
import { routes } from '../../../routes/routes.ts';

type MenuLinkClickedMessage = {
    type: 'MENU_LINK_CLICKED';
    requestId?: string;
};

const getMenuOrigin = (menuUrl: string | undefined) => {
    if (!menuUrl) {
        return undefined;
    }

    try {
        return new URL(menuUrl).origin;
    } catch {
        return undefined;
    }
};

const AppHeader = () => {
    const [appMenuOpen, setAppMenuOpen] = useState(false);
    const [appMenuResetKey, setAppMenuResetKey] = useState(0);

    const [navItemKey, setNavItemKey] = useState('unknown');
    const [currentNavItem, setCurrentNavItem] = useState<ReactNode>(null);

    const location = useLocation();

    useEffect(() => {
        if (location.pathname === routes.ASSETS) {
            setNavItemKey('assets');
            setCurrentNavItem(<FormattedMessage id={'assets-helpgang.application-header.assets'} />);
        } else if (location.pathname === routes.IOT_EVENTS) {
            setNavItemKey('iot-events');
            setCurrentNavItem(<FormattedMessage id={'assets-helpgang.sublink.iot-events'} />);
        } else if (location.pathname === routes.RENTAL) {
            setNavItemKey('rental');
            setCurrentNavItem(<FormattedMessage id={'assets-helpgang.sublink.rental'} />);
        } else {
            setNavItemKey('unknown');
            setCurrentNavItem(null);
        }
    }, [location.pathname]);

    const appTitle = <FormattedMessage id={'assets-helpgang.application-header.moduleName'} />;
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const idToken = useAppSelector(getIdToken)!;
    const menuUrl = config.backend.ADMIN_MENU_SERVICE;
    const menuOrigin = getMenuOrigin(menuUrl);
    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;

    const userMenu = <DefaultUserMenu environment={import.meta.env.MODE} disableCookieModal postLogout={postLogout} />;
    const { sendMessage } = usePostMessageSender();

    const closeAppMenu = () => {
        if (!appMenuOpen) {
            return;
        }

        document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        setAppMenuResetKey(current => current + 1);
        setAppMenuOpen(false);
    };

    useIncomingPostMessages({
        origin: menuOrigin ?? '*',
        handler: (event: MessageEvent) => {
            const message = event.data as MenuLinkClickedMessage | undefined;
            if (message?.type !== 'MENU_LINK_CLICKED') {
                return;
            }

            closeAppMenu();

            sendMessage(
                { type: 'MENU_LINK_HANDLED', requestId: message.requestId },
                event.source as WindowProxy | null,
                event.origin
            );
        },
    });

    const appNavigator = menuUrl ? (
        <div className={'iframe-wrapper'}>
            <IframeResizer
                title='Admin Menu'
                src={menuUrl}
                sizeWidth={false}
                sizeHeight={false}
                scrolling
                style={{ width: '1px', minWidth: '100%', height: '60vh' }}
            />
        </div>
    ) : undefined;

    return (
        <ApplicationHeader
            key={appMenuResetKey}
            label={appTitle}
            appNavigator={appNavigator}
            navItems={[
                {
                    key: navItemKey,
                    route: (
                        <div className={'text-medium text-color-dark text-uppercase cursor-default'}>
                            {currentNavItem}
                        </div>
                    ),
                },
            ]}
            actionBarItems={[userMenu]}
            onToggleAppMenu={setAppMenuOpen}
        />
    );
};

export default AppHeader;
